import {gql} from '@apollo/client';

export const GET_ALL_USERS_NAMES = gql`
  query GetAllUsers($filters: GetUserFilters) {
    getAllUsers(filters: $filters) {
      id
      firstName
      lastName
    }
  }
`;

export const DOWNLOAD_ALL_USERS = gql`
  query DownloadAllUsers($email: String!, $match: GetUserFilters) {
    downloadAllUsers(email: $email, match: $match) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ReturnedMessage {
        response
      }
    }
  }
`;

export const GET_ALL_ADMIN_USERS = gql`
  query GetAllUsers($filters: GetUserFilters) {
    getAllUsers(filters: $filters) {
      users {
        id
        email
        activeAdminTab {
          name
          id
        }
      }
    }
  }
`;

export const GET_ALL_USERS = gql`
  query GetAllUsers($filters: GetUserFilters) {
    getAllUsers(filters: $filters) {
      hasNextPage
      hasPrevPage
      totalDocs
      users {
        id
        firstName
        username
        email
        lastName
        isDeleted
        imageUrl
        phoneNumber
        countryCode
        createdAt
        preferredDisplayCurrency
        profileCompletion {
          completionPercentage
        }
        source
        currentSessionDetails {
          currentDateTime
          medium
        }
        regionCategory {
          region
          subRegion
        }
      }
    }
    statistics: getAllUsers {
      statistics {
        totalAccreditedUsers
        totalActiveInvestors
        totalUsers
        totalVerifiedInvestors
      }
    }
  }
`;

export const GET_INVESTOR_PROFILE = gql`
  query GetInvestorProfile($getInvestorProfileId: ID) {
    getInvestorProfile(id: $getInvestorProfileId) {
      ... on Error {
        message
        statusCode
      }
      ... on InvestorProfileType {
        investorProfile {
          riskNeed {
            investmentGoals {
              betterBudgetting
              stockRewards
              emergencySavings
              retirementSavings
              wealthBuilding
              education
              socialImpact
            }
            investmentHorizon
            annualReturnTarget
            accountUsage
          }
          riskCapacity {
            retirementSavings
            age
            singleAnnualIncome
            jointAnnualNetIncome
            employmentStatus
            taxFilingStatus
            estimatedNetWorth
            investmentHorizon
            investmentTarget
            liquidityNeeds
          }
          isAccredited
          accreditationStatus {
            selfDeclaredAccreditation
            thirdPartyAccreditation
          }
          riskAppetite {
            investmentPreference
            investmentExperience
            selfCategorization
            experienceWithLosses
          }
          affiliations
        }
      }
    }
  }
`;

export const GET_USER_WALLET_DETAILS = gql`
  query AdminGetUserWalletList($input: WalletListInput) {
    adminGetUserWalletList(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on UserWalletList {
        totalBalanceInDisplayCurrency
        wallets {
          id
          currency
          currencySymbol
          currencyName
          status
          colorCode
          lockedCardDepositAmount
          totalDeposits
          balance {
            availableBalance
            availableForInvesting
            lockedBalance
            ledgerBalance
            pendingDepositBalance
            pendingWithdrawalBalance
          }
          accountFlags {
            isFlagged
            notes
            reason
          }
          user {
            id
            firstName
            lastName
            email
          }
        }
      }
    }
  }
`;

export const GET_USER_WALLET_LIMITS = gql`
  query GetUserWalletLimits($getUserByIdId: ID!) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          id
          firstName
          lastName
          limits {
            deposit {
              daily
              weekly
              monthly
              minDepositAmount
              enabled
              maxDepositAmount
              maxNumberOfDepositPerDay
            }
            withdrawal {
              daily
              weekly
              monthly
              minWithdrawalAmount
              enabled
              maxWithdrawalAmount
              maxNumberOfWithdrawalPerDay
            }
          }
        }
      }
    }
  }
`;

export const GET_KYC_FIELDS = gql`
  query GetUserById($getUserByIdId: ID!, $userId: ID) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          id
          firstName
          middleName
          legalFirstName
          legalMiddleName
          legalLastName
          countryISO
          kycCountryISO
          lastName
          email
          language
          dateOfBirth
          complyCubeClientId
          investorProfile {
            isKycApproved(userId: $userId)
            identityCard(userId: $userId) {
              rejectionCode
              amlStatus
              documentCheckStatus
              kycStatus
              rejectionMessages
            }
          }
        }
      }
    }
  }
`;

// Isolated from GET_KYC_FIELDS on purpose: the identity document type and the
// manual submission are supplementary, so a schema mismatch here must never
// break the KYC status form.
//
// `identityCard` takes no userId here: the resolver reads it from the parent
// investor profile. `documents.signedUrl` is a 15 minute presigned GET that
// only resolves for callers with role ADMIN — it is null for everyone else,
// and it must be refetched rather than cached.
export const GET_USER_IDENTITY_DOCUMENT = gql`
  query GetUserIdentityDocument($getUserByIdId: ID!) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          id
          countryISO
          investorProfile {
            identityCard {
              provider
              documentType
              providerDocumentType
              kycInitiatedAt
              documentCountryISO
              documentData {
                fullName
                dateOfBirth
                idNumber
                idType
                issuingCountryISO
                expirationDate
                source
                receivedAt
              }
              residentialAddress {
                line1
                line2
                city
                state
                postalCode
                countryISO
                capturedAt
              }
              nameSync {
                syncedAt
                matched
                documentName
              }
              documents {
                documentSide
                documentType
                fileName
                bucket
                signedUrl
              }
              smileSubmission {
                status
                jobId
                attempts
                lastAttemptAt
                lastError
                submittedAt
                triggeredBy
                verdict {
                  outcome
                  resultCode
                  receivedAt
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Manual KYC review queue, ordered by submission time (kycInitiatedAt) newest
// first. `provider` stays inlined because the queue is only ever the manual one,
// and smileSubmissionStatus narrows to where a card has got to with Smile. A
// card leaves the PENDING_VERIFICATION queue the moment a reviewer decides it —
// it is not deleted, just filed under its new status, so the status filters
// below are how you get at the decided history.
//
// The singular `kycStatus` argument is gone on purpose. It only ever restricted
// the identity check, and supplying it alongside `filters.kycStatuses` applies
// both restrictions — so an AML-only search would still have been silently
// pinned to awaiting-review identity cards.
//
// `filters` carries the attribute filters the two queues share — document type,
// country, region and the document's own nationality and region — plus the
// identity/document/AML status lists and the deposit-status list. They are all
// optional and AND together; values inside one list are OR-ed.
//
// The field returns a union and is paginated (limit defaults to 20, capped at
// 100 server-side), so both branches have to be selected.
export const GET_MANUAL_KYC_QUEUE = gql`
  query ManualKycQueue(
    $page: Int
    $limit: Int
    $filters: KycVerificationFilters
    $smileSubmissionStatus: ManualKycSmileSubmissionStatus
  ) {
    getKycVerificationRequests(
      provider: MANUAL
      page: $page
      limit: $limit
      filters: $filters
      smileSubmissionStatus: $smileSubmissionStatus
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on KycVerificationRequests {
        page
        totalDocs
        hasNextPage
        hasPrevPage
        requests {
          id
          kycInitiatedAt
          providerDocumentType
          documentType
          kycStatus
          documentCheckStatus
          amlStatus
          user {
            id
            firstName
            lastName
            email
            countryISO
            language
          }
          smileSubmission {
            status
            attempts
            lastError
            submittedAt
            triggeredBy
            verdict {
              outcome
              resultCode
            }
          }
        }
      }
    }
  }
`;

// Automatic KYC queue: the current identity card of every user who went through
// a provider — ComplyCube or Smile ID — rather than the manual fallback. Manual
// cards are excluded server-side, including manual documents later sent to Smile
// for an advisory verdict, and legacy cards with no provider count as ComplyCube.
//
// These are current records, not one row per provider attempt. NOT_STARTED
// covers identity cards that exist but have not begun verification; users with
// no identity card at all never appear.
//
// Every filter is optional and they combine with AND, while the values inside a
// list combine with OR. Identity, AML and document checks are independent: an
// approved identity check says nothing about the other two. The same five
// attribute filters the manual queue takes — document type, country, region and
// the document's own nationality and region — go in this input too.
export const GET_AUTOMATIC_KYC_QUEUE = gql`
  query AutomaticKycQueue(
    $filters: AutomaticKycVerificationFilters
    $page: Int
    $limit: Int
  ) {
    getAutomaticKycVerificationRequests(
      filters: $filters
      page: $page
      limit: $limit
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on KycVerificationRequests {
        page
        totalDocs
        hasNextPage
        hasPrevPage
        requests {
          id
          provider
          kycStatus
          amlStatus
          documentCheckStatus
          providerDocumentType
          documentType
          kycInitiatedAt
          isDocumentVerified
          isSelfieVerified
          rejectionCode
          rejectionMessages
          user {
            id
            firstName
            lastName
            email
            countryISO
            language
            complyCubeClientId
          }
        }
      }
    }
  }
`;

export const CHECK_IF_USER_IS_SIM_MODE = gql`
  query GetUserById($getUserByIdId: ID!) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          id
          firstName
          lastName
          toggledSimulationMode
        }
      }
    }
  }
`;

export const GET_USER_COUNTRY_DETAILS = gql`
  query GetUserById($getUserByIdId: ID!) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          countryOfBirth {
            id
            name
          }
          countryOfCitizenship {
            id
            name
          }
          countryOfInterest {
            id
            name
            icon
            type
          }
          topics {
            id
            name
            icon
            type
          }
        }
      }
    }
  }
`;

export const GET_USER_BY_ID = gql`
  query GetUserById($getUserByIdId: ID!, $userId: ID) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          id
          firstName
          middleName
          lastName
          legalFirstName
          legalLastName
          legalMiddleName
          email
          bio
          complyCubeClientId
          internalTeamMember
          createdAt
          referralChannel
          accountId
          referredBy {
            id
            firstName
            lastName
          }
          phoneNumber
          proSubscription {
            hasProSubscription
          }
          dateOfBirth
          preferredDisplayCurrency
          language
          isAccountLocked
          homeAddress
          userSegments {
            name
            description
          }
          homeAddressNumber
          referralCode
          source
          currentSessionDetails {
            currentDevice
            currentAppVersion
            currentTimeZone
            medium
            currentDateTime
          }
          state
          city
          detailsOfAccountClosure
          reasonForClosingAccount
          postalCode
          isDeleted
          username
          middleName
          imageUrl
          trustedContact {
            firstName
            middleName
            lastName
            phoneNumber
            countryCode
            trustedContactPicture
            relationship
            email
            documentType
            documentId
          }
          bannerImage
          citizenStatus
          zipCode
          isInvestorProfileEnabled
          visaType

          countryCode
          isEmailVerified
          country
          numOfFollowers
          numOfFollowing

          notificationPreferences {
            emailNotifications {
              receiveDailyInvestorUpdates
              receiveOtherEmailNotifications
            }
            pushNotifications {
              receiveDailyInvestorUpdates
              receiveOtherPushNotifications
            }
            smsNotifications {
              receiveDailyInvestorUpdates
              receiveOtherSmsNotifications
            }
          }
          investorProfile {
            riskProfile {
              name
            }
            isKycApproved(userId: $userId)
            identityCard(userId: $userId) {
              amlStatus
              kycStatus
              provider
            }
            riskNeed {
              investmentGoals {
                betterBudgetting
                stockRewards
                emergencySavings
                retirementSavings
                wealthBuilding
                education
                socialImpact
              }
              investmentHorizon
              annualReturnTarget
              accountUsage
            }
            riskCapacity {
              retirementSavings
              age
              singleAnnualIncome
              jointAnnualNetIncome
              employmentStatus
              taxFilingStatus
              estimatedNetWorth
              investmentHorizon
              investmentTarget
              liquidityNeeds
            }
            isAccredited
            accreditationStatus {
              selfDeclaredAccreditation
              thirdPartyAccreditation
            }
            riskAppetite {
              investmentPreference
              investmentExperience
              selfCategorization
              experienceWithLosses
            }
          }
        }
      }
    }
  }
`;

export const GET_USER_SECURITY_DETAILS = gql`
  query GetUserById($getUserByIdId: ID!) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          firstName
          lastName
          flagged
          isAccountLocked
        }
      }
    }
  }
`;

export const CHECK_USER_OTP_BLACKLIST = gql`
  query CheckUserOtpBlacklist($input: CheckUserOTPBlackListInput!) {
    checkUserOtpBlacklist(input: $input) {
      __typename
      ... on ResponseWithOtpBlacklistStatus {
        message
        data {
          userId
          isBlacklisted
          mongoBlacklisted
          redisUserBlacklisted
          redisAuthBlacklisted
          mongoEntries {
            id
            userId
            countryCode
            createdAt
            updatedAt
          }
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const DELETE_USER = gql`
  mutation AdminCloseUserAccount($data: AdminCloseUserAccountInput!) {
    adminCloseUserAccount(data: $data) {
      ... on Error {
        message
        statusCode
      }
      ... on ReturnedMessage {
        message
      }
    }
  }
`;

export const RESTORE_USER_ACCOUNT = gql`
  mutation AdminRestoreUserAccount($data: AdminRestoreUserAccountInput!) {
    adminRestoreUserAccount(data: $data) {
      ... on Error {
        message
        statusCode
      }
      ... on ReturnedMessage {
        message
      }
    }
  }
`;

export const GET_USER_ACCOUNT_CLOSURE_DETAILS = gql`
  query User($getUserByIdId: ID!) {
    getUserById(id: $getUserByIdId) {
      ... on UserType {
        user {
          id
          accountId
          firstName
          lastName
          legalFirstName
          legalLastName
          email
          createdAt
          isDeleted
          deletedAt
          reasonForClosingAccount
          detailsOfAccountClosure
          adminAccountClosureFiles {
            name
            url
            type
          }
          adminAccountRestorationFiles {
            name
            url
            type
          }
        }
      }
    }
  }
`;

export const GET_USER_ACCOUNT_CLOSURE_HISTORY = gql`
  query GetAccountClosureHistory($userId: ID!) {
    getAccountClosureHistory(userId: $userId) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithAccountClosureHistory {
        data {
          id
          action
          initiatorType
          reason
          details
          closedAt
          files {
            name
            url
            type
          }
        }
      }
    }
  }
`;

export const GET_USERS_CHART_DATA = gql`
  query GetUsersChartData {
    getUsersSignupChartData {
      daily {
        total
        createdAt
      }
      monthly {
        createdAt
        total
      }
      weekly {
        createdAt
        total
      }
      yearly {
        createdAt
        total
      }
    }
  }
`;

export const GET_RECENT_USERS = gql`
  query GetRecentUsers($page: Int, $limit: Int) {
    getRecentUsers(page: $page, limit: $limit) {
      totalDocs
      users {
        id
        firstName
        middleName
        email
        username
        investorProfile {
          isAccredited
        }
        imageUrl
        isEmailVerified
        profileCompletion {
          completionPercentage
          step {
            title
            completed
          }
        }
      }
    }
  }
`;

export const GET_HOME_OVERVIEW_DATA = gql`
  query GetHomeOverview {
    getTotalNumberOfCompanies
    getAllUsers {
      statistics {
        totalActiveInvestors
        totalUsers
      }
    }
  }
`;

export const GET_USERS_SIGNUP_CHART_DATA = gql`
  query GetUsersSignupChartData {
    getUsersSignupChartData {
      daily {
        total
        createdAt
      }
      monthly {
        createdAt
        total
      }
      weekly {
        createdAt
        total
      }
      yearly {
        createdAt
        total
      }
    }
  }
`;

export const GET_USERS_DAILY_SIGNUP_CHART_DATA = gql`
  query GetUsersDailySignupChartData($month: Int!, $year: Int!) {
    getUsersDailySignupChartData(month: $month, year: $year) {
      daily {
        createdAt
        total
      }
    }
  }
`;

export const GET_ALL_COUNTRIES = gql`
  query GetAllCountries {
    getAllCountries {
      ... on Error {
        message
        statusCode
      }
      ... on Country {
        id
        name
        countryCode
        icon
      }
    }
  }
`;

export const GET_ALL_WHATSAPP_TEMPLATES = gql`
  query GetWhatsappTemplate($language: SourceLanguage!) {
    getWhatsappTemplate(language: $language) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on WhatsappTemplateResponse {
        whatsappTemplates {
          templateVariables {
            name
            isAppLink
            description
          }
          templateType
          templateTitle {
            title
            hasVariable
          }
          templateName
          templateLink {
            url
            type
            isStatic
          }
          templateContent
          id
          hasNameVariable
        }
      }
    }
  }
`;

export const GET_WHATSAPP_TEMPLATE_BY_NAME = gql`
  query GetWhatsappTemplateByName($name: String!, $language: SourceLanguage!) {
    getWhatsappTemplateByName(name: $name, language: $language) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on WhatsappTemplateByIdResponse {
        whatsappTemplate {
          id
          templateTitle {
            title
            hasVariable
          }
          templateName
          templateType
          templateContent
          templateLink {
            isStatic
            type
            url
          }
          templateVariables {
            name
            description
            isAppLink
          }
          hasNameVariable
        }
      }
    }
  }
`;

export const USER_SUBSCRIPTION = gql`
  query GetSubscriptionById($getSubscriptionByIdId: ID) {
    getSubscriptionById(id: $getSubscriptionByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on SubscriptionById {
        subscription {
          userId
          stripeSubscriptionId
          card {
            last4
          }
          subscriptionPrice {
            baseAmount
          }
          currentPeriodStart
          product {
            name
            id
          }
        }
      }
    }
  }
`;

export const GET_USER_SUBSCRIPTIONS = gql`
  query GetUserSubscriptions($userId: ID) {
    getUserSubscriptions(userId: $userId) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithUserSubscriptions {
        data {
          subscriptionType
          subscriptionLabel
          reason
          sourceChannel
          startDate
          paymentMethod
          nextChargeDate
          isRecurring
          isActive
          id
          endDate
          currency
          createdAt
          catalogKey
          appliedCouponCode
          amount
        }
      }
    }
  }
`;

export const GET_SUBSCRIPTION_TRANSACTION_HISTORY = gql`
  query GetSubscriptionTransactionHistory(
    $filter: SubscriptionTransactionHistoryFilter
  ) {
    getSubscriptionTransactionHistory(filter: $filter) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSubscriptionTransactionHistory {
        pagination {
          total
          page
        }
        data {
          id
          subscriptionType
          subscriptionCategory
          catalogKey
          reason
          sourceChannel
          paymentMethod
          appliedCouponCode
          originalAmount
          discountAmount
          subscriptionCurrency
          startDate
          endDate
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const GET_USER_TRADE_BY_ID = gql`
  query GetTradeById($tradeId: ID!) {
    getTradeById(tradeId: $tradeId) {
      ... on Error {
        message
        statusCode
      }
      ... on Trade {
        id
        paymentMethod
        transactionUnits
        transactionStatus
        fundStatus
        transactionAmount
        type
        userId
        offering
        walletAddress
        cryptoCoinType
        campaignId
      }
    }
  }
`;

export const USER_TRANSACTIONS = gql`
  query GetUserById($getUserByIdId: ID!, $userId: ID) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          id
          transactions(userId: $userId) {
            id
            fundraisingCompany {
              companyName
              logoImgURL
            }
            paymentMethod
            transactionAmount
            bankAccountName
            campaignId
            investedAmount
            investedAmountInTransactionCurrency
            assetCurrency
            transactionFees
            symbol
            transactionStatus
            transactionCurrencyToAssetCurrencyFx
            transactionCurrency
            tradeId
            fundStatus
            transactionUnits
            createdAt
          }
        }
      }
    }
  }
`;

export const GET_USERS_FOLLOWING_COMPANY = gql`
  query GetAllUsersFollowingCompany($filter: FollowCompanyFilter!) {
    getAllUsersFollowingCompany(filter: $filter) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithUsers {
        data {
          firstName
          lastName
          username
          email
          id
          imageUrl
        }
        pagination {
          hasNextPage
          hasPrevPage
        }
      }
    }
  }
`;

export const GET_USER_SESSION_RECORDINGS = gql`
  query GetUserSessionRecordings($filter: UserSessionRecordingFilter) {
    getUserSessionRecordings(filter: $filter) {
      data {
        id
        sessionUrl
        updatedAt
        createdAt
      }
      pagination {
        total
      }
    }
  }
`;

export const GET_USER_PORTFOLIO = gql`
  query AdminGetUserPortfolio(
    $displayCurrency: CurrencyCode!
    $userId: ID!
    $appMode: AppMode
  ) {
    adminGetUserPortfolio(
      displayCurrency: $displayCurrency
      userId: $userId
      appMode: $appMode
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on AdminUserPortfolioType {
        investments {
          displayCurrency
          totalBondsInvested
          totalStocksInvestmentAmount
          totalVentureFundInvestmentAmount
          totalBondsInvestmentAmount
          totalVentureInvestmentAmount
          totalMutualFundInvestmentAmount
          totalVentureInvestmentAmount
          displayTotal
          totalStocksPortfolioPercentage
          totalBondsPortfolioPercentage
          totalMutualFundPortfolioPercentage
          totalVentureFundPortfolioPercentage
          totalVenturePortfolioPercentage
          portfolioWeight
          portfolioRiskScore
          stocks {
            id
            totalPercentageChange
            transactionUnits
            company {
              name
              ticker
            }
            currentAmountInDisplayCurrency
          }
          ventures {
            internalInvestments {
              id
              totalPercentageChange
              transactionUnits
              company {
                name
                ticker
              }
              currentAmountInDisplayCurrency
            }
            externalInvestments {
              id
              transactionUnits
              company {
                name
                ticker
              }
            }
          }
          ventureFunds {
            id
            totalPercentageChange
            transactionUnits
            company {
              name
              ticker
            }
            currentAmountInDisplayCurrency
          }
          bonds {
            id
            totalPercentageChange
            transactionUnits
            company {
              name
              ticker
            }
            currentAmountInDisplayCurrency
          }
          mutualFunds {
            id
            totalPercentageChange
            transactionUnits
            company {
              name
              ticker
            }
            currentAmountInDisplayCurrency
          }
        }
      }
    }
  }
`;

export const GET_USER_INTERACTIONS = gql`
  query GetInteractions($filter: InteractionFilter!) {
    getInteractions(filter: $filter) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInteractions {
        pagination {
          total
        }
        data {
          id
          user {
            firstName
            lastName
            email
          }
          type
          date
          note
          files {
            name
            url
            type
          }
          outcomes {
            kycStatus
            depositStatus
            depositAmount
            investmentStatus
            depositCurrency
            investmentCurrency
            investmentAmount
            dabaProCurrency
            dabaProStatus
            dabaProPlan
            dabaAcademyStatus
            dabaAcademyCurrency
            dabaAcademyPlan
          }
          createdAt
          updatedAt
          createdBy {
            firstName
            lastName
            email
          }
          files {
            name
            url
            type
          }
          transcript {
            text
            speaker
            startTime
            endTime
            durationMs
          }
          summary
          sentimentLabel
          sentimentScore
          actionItems {
            id
            title
            description
            category
            priority
            dueDate
            status
          }
        }
      }
    }
  }
`;

export const GET_USER_ACTION_ITEMS = gql`
  query GetInteractionsWithPendingActionItems($userId: ID!) {
    getInteractionsWithPendingActionItems(userId: $userId) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInteractions {
        data {
          actionItems {
            id
            title
            description
            category
            priority
            dueDate
            status
          }
          id
        }
      }
    }
  }
`;

export const UPDATE_ACTION_ITEM = gql`
  mutation UpdateActionItemStatus(
    $interactionId: ID!
    $actionItem: UpdateActionItemStatusInput!
  ) {
    updateActionItemStatus(
      interactionId: $interactionId
      actionItem: $actionItem
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithInteraction {
        message
      }
    }
  }
`;

export const GET_ALL_RISK_PROFILES = gql`
  query GetAllRiskProfiles {
    getAllRiskProfiles {
      ... on ResponseWithRiskProfiles {
        data {
          id
          name
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const GET_USER_LIENS = gql`
  query GetLiens($input: LienFilterInput) {
    getLiens(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithLiens {
        pagination {
          total
        }
        data {
          updatedAt
          status
          type
          reason
          id
          currency
          creator {
            firstName
            lastName
            email
          }
          createdAt
          amount
        }
      }
    }
  }
`;

export const PLACE_USER_LIEN = gql`
  mutation PlaceLien($input: PlaceLienInput!) {
    placeLien(input: $input) {
      ... on Error {
        statusCode
        errorMessage: message
      }
      ... on ResponseWithLien {
        message
      }
    }
  }
`;

export const DELETE_USER_LIEN = gql`
  mutation RemoveLien($input: RemoveLienInput!) {
    removeLien(input: $input) {
      ... on Error {
        statusCode
        errorMessage: message
      }
      ... on ResponseWithLien {
        message
      }
    }
  }
`;

export const GET_KYC_DASHBOARD_DATA_STATS = gql`
  query GetUserKycStatistics {
    getUserKycStatistics {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithKycStatistics {
        data {
          totalKyc {
            identityCheck
            documentCheck
            aml
          }
          successfulKyc {
            identityCheck
            documentCheck
            aml
          }
          pendingKyc {
            identityCheck
            documentCheck
            aml
          }
          failedKyc {
            identityCheck
            documentCheck
            aml
          }
          notStartedKyc {
            identityCheck
            documentCheck
            aml
          }
        }
      }
    }
  }
`;

export const KYC_DASHBOARD_DATA_TABLE = gql`
  query GetAllUsers($filters: GetUserFilters) {
    getAllUsers(filters: $filters) {
      totalDocs
      users {
        id
        firstName
        lastName
        email
        country
        investorProfile {
          isKycApproved
          identityCard {
            amlStatus
            documentCheckStatus
            kycStatus
          }
        }
      }
    }
  }
`;

export const GET_WAIT_LIST_DATA = gql`
  query GetWaitlistEntries($filters: WaitlistFilters) {
    getWaitlistEntries(filters: $filters) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithWaitlistEntries {
        pagination {
          total
        }
        data {
          user {
            firstName
            lastName
            email
          }
          createdAt
          countryWaitListNumber
          country
          id
          globalWaitListNumber
          lastAccessedFrom {
            ip
            country
          }
          ipAddress
          hasAccessedApp
          firstAccessedFrom {
            ip
            country
          }
        }
      }
    }
  }
`;

export const GET_USER_PROFILE_UPDATE_HISTORY = gql`
  query GetUserProfileUpdateHistory($filter: UserProfileUpdateHistoryFilter) {
    getUserProfileUpdateHistory(filter: $filter) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithUserProfileUpdateHistory {
        message
        pagination {
          page
          limit
          pages
          total
        }
        data {
          id
          action
          source
          fields
          actorEmail
          actorRole
          changes {
            path
            type
            oldValue
            newValue
          }
          createdAt
          user {
            email
            id
          }
        }
      }
    }
  }
`;

/**
 * Just the user's app language, for the manual transaction modal.
 *
 * Its own query rather than a field on one of the fatter GET_USER_BY_ID
 * variants: the modal can be opened for a user picked in the modal itself, so
 * it fetches lazily by id and has no use for the rest of the profile.
 */
export const GET_USER_APP_LANGUAGE = gql`
  query GetUserById($getUserByIdId: ID!) {
    getUserById(id: $getUserByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on UserType {
        user {
          id
          language
        }
      }
    }
  }
`;
